/**
 * OverView trend engine (Round 92).
 *
 * Computes the Up / Down / Neutral trend of 7 indicators for a single
 * timeframe's candle series, then blends them into a weighted -100..+100
 * score. The Charts OverView panel calls this once per timeframe (chart TF
 * and the mapped HTF). All computations reuse the same helpers the chart
 * overlays use, so the trend a row reports matches the drawn indicator.
 *
 * Each indicator is computed self-contained on the passed series (CPR/POC use
 * standard Daily/Weekly/Monthly grouping derived from the interval). The exact
 * line can differ marginally from the drawn overlay in edge cases, but the
 * up/down/neutral signal — all this panel reports — stays consistent.
 */
import {
  CandleRow, computeCpr, computePocPerPeriod, ema, type CprPeriod,
} from './dailyBlastIndicators';
import { computeLRHamaBand } from './signalIndicators';
import { linreg, transformCandles } from './candleTransforms';
import {
  zoneForScore, type OverviewZone, type TrendDir,
} from '../theme/overviewColors';
import { jhmEmaVwma, resolveJhm } from './jhm';
import { wrPctPair, resolveWrPct, wrPctZoneAt } from './wrPct';
import { bolevDirSeries, volDirSeries, avBandDirSeries, resolveBolevLookback } from './overviewExtraVoters';

export const OVERVIEW_INDICATORS = ['ema', 'avgCloud', 'cpr', 'cpoc', 'hpoc', 'relStrength', 'chgN', 'macd', 'jhm', 'wrPct', 'bolevD', 'bolevW', 'vol', 'avBand'] as const;
export type OverviewIndicatorKey = typeof OVERVIEW_INDICATORS[number];

export const INDICATOR_LABELS: Record<OverviewIndicatorKey, string> = {
  ema: 'EMAs',
  avgCloud: 'AvgCloud',
  cpr: 'CPR',
  cpoc: 'C-POC',
  hpoc: 'H-POC',
  relStrength: 'RelStr',
  chgN: 'ChgN%',
  macd: 'MACD',
  jhm: 'JHM',
  wrPct: 'WR%',
  bolevD: 'BO_D',
  bolevW: 'BO_W',
  vol: 'Vol',
  avBand: 'AV_Band',
};

export interface IndicatorTrend {
  dir: TrendDir;
  /** Short human explanation for the tooltip. */
  detail: string;
}

export interface TimeframeTrends {
  perIndicator: Record<OverviewIndicatorKey, IndicatorTrend>;
  /** -100..+100, weighted over the indicators that had data. */
  score: number;
  zone: OverviewZone;
  /** Number of indicators that produced a usable signal. */
  available: number;
}

export interface OverviewWeights {
  ema: number; avgCloud: number; cpr: number; cpoc: number;
  hpoc: number; relStrength: number; chgN: number; macd?: number; jhm?: number; wrPct?: number;
  bolevD?: number; bolevW?: number; vol?: number; avBand?: number;
}

const NA: IndicatorTrend = { dir: 'na', detail: 'not enough data' };

/** Last finite value of a (possibly null/NaN-laced) numeric array. */
function lastFinite(arr: Array<number | null | undefined>): number | null {
  for (let i = arr.length - 1; i >= 0; i--) {
    const v = arr[i];
    if (v != null && Number.isFinite(v)) return v;
  }
  return null;
}

/** Trend from price vs a band (above top = up, below bottom = down, inside = neutral). */
function bandTrend(ltp: number, top: number, bottom: number, label: string, dp = 2): IndicatorTrend {
  const hi = Math.max(top, bottom), lo = Math.min(top, bottom);
  const f = (n: number) => n.toFixed(dp);
  if (ltp > hi) return { dir: 'up', detail: `LTP ${f(ltp)} > ${label} top ${f(hi)}` };
  if (ltp < lo) return { dir: 'down', detail: `LTP ${f(ltp)} < ${label} bottom ${f(lo)}` };
  return { dir: 'neutral', detail: `LTP ${f(ltp)} inside ${label} ${f(lo)}–${f(hi)}` };
}

/** Trend from a line vs its EMA (line above EMA = up). */
function lineVsEmaTrend(line: number, emaVal: number, label: string): IndicatorTrend {
  const f = (n: number) => n.toFixed(2);
  if (line > emaVal) return { dir: 'up', detail: `${label} ${f(line)} > EMA ${f(emaVal)}` };
  if (line < emaVal) return { dir: 'down', detail: `${label} ${f(line)} < EMA ${f(emaVal)}` };
  return { dir: 'neutral', detail: `${label} ${f(line)} = EMA ${f(emaVal)}` };
}

/** CPR/POC grouping for a chart interval. R120.5 — uses the app's standard CPR
 *  base mapping (cprBaseFromLtf) so the CPR / C-POC / H-POC VOTERS group by the
 *  SAME period the drawn CPR/POC overlays use (≤15m → Daily, ≤4h → Weekly,
 *  >4h → Monthly). Previously this used a divergent (≥1440→W, ≥10080→M) mapping,
 *  so on many timeframes the score's pivot/POC period differed from what's drawn. */
function basePeriodForInterval(intervalMin: number): CprPeriod {
  if (intervalMin <= 15) return 'D';
  if (intervalMin <= 240) return 'W';
  return 'M';
}

export interface ComputeTrendsOpts {
  candles: CandleRow[];
  ltp: number | null;
  benchmarkCandles?: CandleRow[] | null;
  intervalMin: number;
  exchange?: string;
  /** Full resolved chart settings (for ema periods, relStrength/dayChange params). */
  settings: any;
  weights: OverviewWeights;
  thresholds: { strong: number; probable: number };
  decimals?: number;
  /** RAW Daily / Weekly candles (IST-shifted) for the BO_D / BO_W voters. */
  dailyCandles?: CandleRow[] | null;
  weeklyCandles?: CandleRow[] | null;
  /** PERF (R127) — precomputed per-bar baseScore (score EXCLUDING avBand) keyed
   *  by bar time. When supplied, the gauge skips recomputing the whole series
   *  just to derive avBand's Effective Line. */
  baseScoreByTime?: Map<number, number>;
}

export function computeTimeframeTrends(opts: ComputeTrendsOpts): TimeframeTrends {
  const { candles, benchmarkCandles, intervalMin, exchange, settings, weights, thresholds } = opts;
  const dp = opts.decimals ?? 2;
  const per = {} as Record<OverviewIndicatorKey, IndicatorTrend>;
  for (const k of OVERVIEW_INDICATORS) per[k] = NA;

  if (!candles || candles.length < 2) {
    return { perIndicator: per, score: 0, zone: zoneForScore(0, thresholds.strong, thresholds.probable), available: 0 };
  }
  const closes = candles.map(c => c.close);
  const ltp = opts.ltp != null && Number.isFinite(opts.ltp) ? opts.ltp : closes[closes.length - 1];

  // CSH-transformed closes power RelStrength + ChgN% (matches the chart overlays).
  const cshCloses = transformCandles(candles, 'CSH').map(c => c.close);

  // a. EMAs #3 / #4 over LinReg(close, 14).
  try {
    const cfg = settings?.emas || {};
    const p3 = Math.max(1, Number(cfg.ema3?.period) || 51);
    const p4 = Math.max(1, Number(cfg.ema4?.period) || 200);
    const lr = linreg(closes, 14).map(v => (Number.isFinite(v) ? v : null));
    const e3 = lastFinite(ema(lr, p3));
    const e4 = lastFinite(ema(lr, p4));
    if (e3 != null && e4 != null) {
      const f = (n: number) => n.toFixed(dp);
      if (ltp > e3 && ltp > e4) per.ema = { dir: 'up', detail: `LTP ${f(ltp)} > EMA${p3} ${f(e3)} & EMA${p4} ${f(e4)}` };
      else if (ltp < e3 && ltp < e4) per.ema = { dir: 'down', detail: `LTP ${f(ltp)} < EMA${p3} ${f(e3)} & EMA${p4} ${f(e4)}` };
      else per.ema = { dir: 'neutral', detail: `LTP ${f(ltp)} between EMA${p3} ${f(e3)} / EMA${p4} ${f(e4)}` };
    }
  } catch { /* leave NA */ }

  // b. AvgCloud — LR_HAMA band (the cloud region).
  try {
    if (candles.length >= 5) {
      const band = computeLRHamaBand(candles);
      const last = band[band.length - 1];
      if (last && Number.isFinite(last.upper) && Number.isFinite(last.lower)) {
        per.avgCloud = bandTrend(ltp, last.upper, last.lower, 'cloud', dp);
      }
    }
  } catch { /* leave NA */ }

  // c. CPR.
  try {
    const cpr = computeCpr(candles, basePeriodForInterval(intervalMin), exchange);
    const last = cpr[cpr.length - 1];
    if (last && Number.isFinite(last.tc) && Number.isFinite(last.bc)) {
      per.cpr = bandTrend(ltp, last.tc, last.bc, 'CPR', dp);
    }
  } catch { /* leave NA */ }

  // d. C-POC (base-period volume POC band).
  try {
    const poc = computePocPerPeriod(candles, basePeriodForInterval(intervalMin), exchange);
    const last = poc[poc.length - 1];
    if (last && Number.isFinite(last.top) && Number.isFinite(last.bottom)) {
      per.cpoc = bandTrend(ltp, last.top, last.bottom, 'C-POC', dp);
    }
  } catch { /* leave NA */ }

  // e. H-POC (one grouping higher than C-POC).
  try {
    const base = basePeriodForInterval(intervalMin);
    const hPeriod: CprPeriod = base === 'D' ? 'W' : 'M';
    const poc = computePocPerPeriod(candles, hPeriod, exchange);
    const last = poc[poc.length - 1];
    if (last && Number.isFinite(last.top) && Number.isFinite(last.bottom)) {
      per.hpoc = bandTrend(ltp, last.top, last.bottom, 'H-POC', dp);
    }
  } catch { /* leave NA */ }

  // f. RelStrength — RS line vs its EMA (needs a benchmark series).
  try {
    if (benchmarkCandles && benchmarkCandles.length > 14 && cshCloses.length > 14) {
      const rsCfg = settings?.relStrength || {};
      const emaPeriod = Math.max(1, Math.floor(Number(rsCfg.emaPeriod) || 7));
      const symLR = linreg(cshCloses, 14);
      const benchCsh = transformCandles(benchmarkCandles, 'CSH').map(c => c.close);
      const benchLR = linreg(benchCsh, 14);
      const benchLRByTime = new Map<number, number>();
      for (let i = 0; i < benchmarkCandles.length; i++) {
        const v = benchLR[i];
        if (v != null && Number.isFinite(v) && v > 0) benchLRByTime.set(benchmarkCandles[i].time, v);
      }
      const rsArr: Array<number | null> = new Array(candles.length).fill(null);
      let firstRatio: number | null = null;
      for (let i = 0; i < candles.length; i++) {
        const symVal = symLR[i];
        const benchVal = benchLRByTime.get(candles[i].time);
        if (symVal == null || benchVal == null || !Number.isFinite(symVal) || benchVal === 0) continue;
        const ratio = symVal / benchVal;
        if (firstRatio === null) firstRatio = ratio;
        rsArr[i] = (ratio / firstRatio) * 100;
      }
      const emaArr = ema(rsArr, emaPeriod);
      const rsLast = lastFinite(rsArr);
      const emaLast = lastFinite(emaArr);
      if (rsLast != null && emaLast != null) per.relStrength = lineVsEmaTrend(rsLast, emaLast, 'RS');
    }
  } catch { /* leave NA */ }

  // g. ChgN% — rolling N-bar % change vs its EMA.
  try {
    const dcCfg = settings?.dayChange || {};
    const lookback = Math.max(1, Math.floor(Number(dcCfg.lookbackBars) || 24));
    const emaPeriod = Math.max(1, Math.floor(Number(dcCfg.emaPeriod) || 7));
    if (cshCloses.length > lookback) {
      const pctArr: Array<number | null> = new Array(candles.length).fill(null);
      for (let i = lookback; i < candles.length; i++) {
        const prev = cshCloses[i - lookback];
        const cur = cshCloses[i];
        if (prev > 0 && Number.isFinite(prev) && Number.isFinite(cur)) {
          pctArr[i] = ((cur - prev) / prev) * 100;
        }
      }
      const emaArr = ema(pctArr, emaPeriod);
      const pctLast = lastFinite(pctArr);
      const emaLast = lastFinite(emaArr);
      if (pctLast != null && emaLast != null) per.chgN = lineVsEmaTrend(pctLast, emaLast, `Chg${lookback}`);
    }
  } catch { /* leave NA */ }

  // h. MACD — EMA(fast) − EMA(slow) on the CSH-smoothed close. Trend follows
  //    the MACD line's SLOPE: rising = up, falling = down (matches the chart
  //    overlay; not the MACD↔Signal crossover).
  try {
    const mcfg = settings?.macd || {};
    const fast = Math.max(1, Math.floor(Number(mcfg.fastLength) || 12));
    const slow = Math.max(1, Math.floor(Number(mcfg.slowLength) || 26));
    if (cshCloses.length > slow + 2) {
      const fastE = ema(cshCloses, fast);
      const slowE = ema(cshCloses, slow);
      const macdArr: Array<number | null> = cshCloses.map((_, i) => {
        const f = fastE[i], s = slowE[i];
        return (f != null && s != null && Number.isFinite(f) && Number.isFinite(s)) ? (f as number) - (s as number) : null;
      });
      // Last two consecutive finite MACD values give the current slope.
      let mLast: number | null = null, mPrev: number | null = null;
      for (let i = macdArr.length - 1; i >= 0; i--) {
        const v = macdArr[i];
        if (v == null || !Number.isFinite(v)) continue;
        if (mLast == null) mLast = v; else { mPrev = v; break; }
      }
      if (mLast != null && mPrev != null) {
        const f = (n: number) => n.toFixed(4);
        if (mLast > mPrev) per.macd = { dir: 'up', detail: `MACD rising (${f(mPrev)} → ${f(mLast)})` };
        else if (mLast < mPrev) per.macd = { dir: 'down', detail: `MACD falling (${f(mPrev)} → ${f(mLast)})` };
        else per.macd = { dir: 'neutral', detail: `MACD flat ${f(mLast)}` };
      }
    }
  } catch { /* leave NA */ }

  // j. WR% — fast/slow Williams-%R EMAs (R116b zone logic).
  //    BUY    when slowEMA > -50 AND fastEMA rising  → 'up'
  //    SHORT  when slowEMA < -50 AND fastEMA falling → 'down'
  //    NEUTRAL→ 'na' (abstain — no score contribution)
  try {
    const wp = resolveWrPct(settings?.wrPct);
    const wSrc = wp.source === 'linregClose'
      ? linreg(closes, 14).map(v => (Number.isFinite(v) ? v : NaN))
      : closes.slice();
    const wHighs = candles.map(c => c.high);
    const wLows  = candles.map(c => c.low);
    const { fast, slow } = wrPctPair(
      wSrc.map(v => Number(v)),
      wHighs.map(v => Number(v)),
      wLows.map(v => Number(v)),
      wp,
    );
    // Find the most recent bar with finite fast/prev-fast/slow.
    let zi = -1;
    for (let i = fast.length - 1; i >= 1; i--) {
      if (Number.isFinite(fast[i]) && Number.isFinite(fast[i - 1]) && Number.isFinite(slow[i])) {
        zi = i; break;
      }
    }
    if (zi >= 1) {
      const f = fast[zi], fPrev = fast[zi - 1], s = slow[zi];
      const zone = wrPctZoneAt(fast, slow, zi);
      const fmt = (n: number) => n.toFixed(2);
      if (zone === 'BUY') per.wrPct = { dir: 'up',   detail: `BUY zone — slow ${fmt(s)} > -50 & fast rising (${fmt(fPrev)} → ${fmt(f)})` };
      else if (zone === 'SHORT') per.wrPct = { dir: 'down', detail: `SHORT zone — slow ${fmt(s)} < -50 & fast falling (${fmt(fPrev)} → ${fmt(f)})` };
      else per.wrPct = { dir: 'na', detail: `NEUTRAL — slow ${fmt(s)}, fast ${fmt(fPrev)} → ${fmt(f)}` };
    }
  } catch { /* leave NA */ }

  // i. JHM (JSR Hilega-Milega) — EMA(rsi) vs VWMA(rsi). Bull when EMA ≥ VWMA.
  try {
    const jp = resolveJhm(settings?.jhm);
    const vols = candles.map(c => c.volume || 0);
    const srcArr = jp.source === 'linregClose'
      ? linreg(closes, 14).map(v => (Number.isFinite(v) ? v : NaN))
      : closes;
    const { ema: jEma, vwma: jVwma } = jhmEmaVwma(srcArr, vols, jp);
    const eLast = lastFinite(jEma), vLast = lastFinite(jVwma);
    if (eLast != null && vLast != null) {
      const f = (n: number) => n.toFixed(2);
      if (eLast > vLast) per.jhm = { dir: 'up', detail: `JHM bull — EMA ${f(eLast)} > VWMA ${f(vLast)}` };
      else if (eLast < vLast) per.jhm = { dir: 'down', detail: `JHM bear — EMA ${f(eLast)} < VWMA ${f(vLast)}` };
      else per.jhm = { dir: 'neutral', detail: `JHM flat — EMA = VWMA ${f(eLast)}` };
    }
  } catch { /* leave NA */ }

  // ── Extra latched/stateful voters (R120): BO_D, BO_W, Vol, AV_Band ─────────
  // Computed as full per-bar series (they carry forward state); the dashboard
  // reports the LAST bar's latch. avBand needs the per-bar score EXCLUDING
  // avBand (the baseScore), obtained from the per-bar series, so its Effective
  // Line is identical to the engine's and the drawn overlay's.
  try {
    const lookback = resolveBolevLookback(settings);
    const dArr = bolevDirSeries(candles, opts.dailyCandles, lookback);
    const wArr = bolevDirSeries(candles, opts.weeklyCandles, lookback);
    const vArr = volDirSeries(candles);
    const dLast = dArr[dArr.length - 1] ?? 'na';
    const wLast = wArr[wArr.length - 1] ?? 'na';
    const vLast = vArr[vArr.length - 1] ?? 'na';
    if (dLast !== 'na') per.bolevD = { dir: dLast, detail: `BO_D ${dLast === 'up' ? 'above BUY line (bullish bias)' : 'below SHORT line (bearish bias)'}` };
    if (wLast !== 'na') per.bolevW = { dir: wLast, detail: `BO_W ${wLast === 'up' ? 'above BUY line (bullish bias)' : 'below SHORT line (bearish bias)'}` };
    if (vLast !== 'na') per.vol = { dir: vLast, detail: `Vol last spike ${vLast === 'up' ? 'GREEN (accumulation)' : 'RED (distribution)'}` };

    // baseScore series (excludes avBand) → avBand voter Fast-HMA Effective Line.
    // R127 PERF — reuse a precomputed baseScore map (from the per-bar series the
    // caller already built) instead of recomputing the WHOLE series here. The
    // internal recompute made every gauge call an O(n) series pass (×2 for
    // TF+HTF, on top of the standalone ovSeries memos) → slow symbol switches.
    let baseScoreByTime = opts.baseScoreByTime;
    if (!baseScoreByTime) {
      baseScoreByTime = new Map<number, number>();
      const baseSeries = computeTimeframeTrendSeries({
        candles, benchmarkCandles, intervalMin, exchange, settings, weights, thresholds,
        dailyCandles: opts.dailyCandles, weeklyCandles: opts.weeklyCandles,
      });
      for (const p of baseSeries) if (Number.isFinite(p.scoreBase)) baseScoreByTime.set(p.time, p.scoreBase);
    }
    const avArr = avBandDirSeries(candles, baseScoreByTime, settings);
    const avLast = avArr[avArr.length - 1] ?? 'na';
    if (avLast !== 'na') per.avBand = { dir: avLast, detail: `AV_Band close ${avLast === 'up' ? '> Fast HMA (bull)' : avLast === 'down' ? '< Fast HMA (bear)' : '= Fast HMA'}` };
  } catch { /* leave the four NA */ }

  // ── Weighted score over available indicators ──────────────────────────────
  let wSum = 0, sSum = 0, available = 0;
  for (const k of OVERVIEW_INDICATORS) {
    const t = per[k];
    if (t.dir === 'na') continue;
    const w = Math.max(0, Number((weights as any)[k]) || 0);
    if (w === 0) continue;
    const signal = t.dir === 'up' ? 1 : t.dir === 'down' ? -1 : 0;
    wSum += w;
    sSum += w * signal;
    available++;
  }
  const score = wSum > 0 ? Math.round((sSum / wSum) * 100) : 0;
  const zone = zoneForScore(score, thresholds.strong, thresholds.probable);
  return { perIndicator: per, score, zone, available };
}

// ─────────────────────────── Per-bar trend series ───────────────────────────
// Round 96 — the crosshair tooltip shows the OverView score of the HOVERED
// candle (not just the latest). This computes a -100..+100 score per bar by
// building each indicator's full array ONCE (O(n)) and evaluating the bar's
// close vs that bar's levels — same rules as computeTimeframeTrends.

export interface TrendSeriesPoint {
  time: number;
  /** Final score INCLUDING the avBand voter. */
  score: number;
  /** Score EXCLUDING the avBand voter — feeds avBand's Effective Line and the
   *  drawn AV_Band overlay (breaks the avBand↔score circular dependency). */
  scoreBase: number;
  zone: OverviewZone;
  up: number;
  down: number;
  na: number;
}

export function computeTimeframeTrendSeries(opts: {
  candles: CandleRow[];
  benchmarkCandles?: CandleRow[] | null;
  intervalMin: number;
  exchange?: string;
  settings: any;
  weights: OverviewWeights;
  thresholds: { strong: number; probable: number };
  dailyCandles?: CandleRow[] | null;
  weeklyCandles?: CandleRow[] | null;
}): TrendSeriesPoint[] {
  const { candles, benchmarkCandles, intervalMin, exchange, settings, weights, thresholds } = opts;
  if (!candles || candles.length < 5) return [];
  const n = candles.length;
  const closes = candles.map(c => c.close);
  const cshCloses = transformCandles(candles, 'CSH').map(c => c.close);
  const W = weights as any;

  // EMA #3/#4 over linreg(close, 14).
  const cfg = settings?.emas || {};
  const p3 = Math.max(1, Number(cfg.ema3?.period) || 51);
  const p4 = Math.max(1, Number(cfg.ema4?.period) || 200);
  const lr = linreg(closes, 14).map(v => (Number.isFinite(v) ? v : null));
  const e3 = ema(lr, p3);
  const e4 = ema(lr, p4);

  // AvgCloud band keyed by time.
  const bandByTime = new Map<number, { hi: number; lo: number }>();
  try {
    for (const b of computeLRHamaBand(candles)) {
      if (Number.isFinite(b.upper) && Number.isFinite(b.lower)) {
        bandByTime.set(b.time, { hi: Math.max(b.upper, b.lower), lo: Math.min(b.upper, b.lower) });
      }
    }
  } catch { /* ignore */ }

  // CPR / C-POC / H-POC per-period points (sparse) — merge-walked below.
  const base = basePeriodForInterval(intervalMin);
  const hPeriod: CprPeriod = base === 'D' ? 'W' : 'M';
  let cprPts: ReturnType<typeof computeCpr> = [];
  let cpocPts: ReturnType<typeof computePocPerPeriod> = [];
  let hpocPts: ReturnType<typeof computePocPerPeriod> = [];
  try { cprPts = computeCpr(candles, base, exchange); } catch { /* ignore */ }
  try { cpocPts = computePocPerPeriod(candles, base, exchange); } catch { /* ignore */ }
  try { hpocPts = computePocPerPeriod(candles, hPeriod, exchange); } catch { /* ignore */ }

  // RelStrength array + its EMA.
  const rsArr: Array<number | null> = new Array(n).fill(null);
  let rsEma: Array<number | null> = new Array(n).fill(null);
  if (benchmarkCandles && benchmarkCandles.length > 14) {
    try {
      const rsEmaP = Math.max(1, Math.floor(Number(settings?.relStrength?.emaPeriod) || 7));
      const symLR = linreg(cshCloses, 14);
      const benchLR = linreg(transformCandles(benchmarkCandles, 'CSH').map(c => c.close), 14);
      const benchByTime = new Map<number, number>();
      for (let i = 0; i < benchmarkCandles.length; i++) {
        const v = benchLR[i];
        if (v != null && Number.isFinite(v) && v > 0) benchByTime.set(benchmarkCandles[i].time, v);
      }
      let firstRatio: number | null = null;
      for (let i = 0; i < n; i++) {
        const s = symLR[i], b = benchByTime.get(candles[i].time);
        if (s == null || b == null || !Number.isFinite(s) || b === 0) continue;
        const ratio = s / b;
        if (firstRatio == null) firstRatio = ratio;
        rsArr[i] = (ratio / firstRatio) * 100;
      }
      rsEma = ema(rsArr, rsEmaP);
    } catch { /* ignore */ }
  }

  // ChgN% array + its EMA.
  const lookback = Math.max(1, Math.floor(Number(settings?.dayChange?.lookbackBars) || 24));
  const dcEmaP = Math.max(1, Math.floor(Number(settings?.dayChange?.emaPeriod) || 7));
  const pctArr: Array<number | null> = new Array(n).fill(null);
  for (let i = lookback; i < n; i++) {
    const prev = cshCloses[i - lookback], cur = cshCloses[i];
    if (prev > 0 && Number.isFinite(prev) && Number.isFinite(cur)) pctArr[i] = ((cur - prev) / prev) * 100;
  }
  const pctEma = ema(pctArr, dcEmaP);

  // MACD array (slope-based).
  const mfast = Math.max(1, Math.floor(Number(settings?.macd?.fastLength) || 12));
  const mslow = Math.max(1, Math.floor(Number(settings?.macd?.slowLength) || 26));
  const mFastE = ema(cshCloses, mfast);
  const mSlowE = ema(cshCloses, mslow);
  const macdArr: Array<number | null> = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const f = mFastE[i], s = mSlowE[i];
    if (f != null && s != null && Number.isFinite(f) && Number.isFinite(s)) macdArr[i] = (f as number) - (s as number);
  }

  // JHM arrays — EMA(rsi) & VWMA(rsi); bar i votes bull when EMA ≥ VWMA.
  const jp = resolveJhm(settings?.jhm);
  const jVols = candles.map(c => c.volume || 0);
  const jSrc = jp.source === 'linregClose'
    ? linreg(closes, 14).map(v => (Number.isFinite(v) ? v : NaN))
    : closes;
  const { ema: jEmaArr, vwma: jVwmaArr } = jhmEmaVwma(jSrc, jVols, jp);

  // WR% arrays — fast/slow Williams-%R EMAs. Zone (R116b):
  //   BUY    when slowEMA > -50 and fastEMA is rising  → 'up'
  //   SHORT  when slowEMA < -50 and fastEMA is falling → 'down'
  //   NEUTRAL otherwise → 'na' (abstain)
  // Highs/lows always come from raw OHLC (Williams %R defines the bar's
  // range explicitly); source choice only affects the close series used
  // in the %R numerator.
  const wp = resolveWrPct(settings?.wrPct);
  const wSrc = wp.source === 'linregClose'
    ? linreg(closes, 14).map(v => (Number.isFinite(v) ? v : NaN))
    : closes;
  const wHighs = candles.map(c => c.high);
  const wLows  = candles.map(c => c.low);
  const { fast: wrFast, slow: wrSlow } = wrPctPair(
    wSrc.map(v => Number(v)),
    wHighs.map(v => Number(v)),
    wLows.map(v => Number(v)),
    wp,
  );

  // Extra latched voters (R120). BO_D / BO_W / Vol are score-independent, so we
  // build them up-front; avBand needs the baseScore (computed in pass 1) and is
  // folded in during pass 2 — this two-pass split breaks the circular
  // dependency (avBand's Effective Line is derived from the OverView score).
  const boLookback = resolveBolevLookback(settings);
  const bolevDArr = bolevDirSeries(candles, opts.dailyCandles, boLookback);
  const bolevWArr = bolevDirSeries(candles, opts.weeklyCandles, boLookback);
  const volArr = volDirSeries(candles);

  // ── Pass 1: base score (all voters EXCEPT avBand) ──────────────────────────
  interface Base { time: number; wSum: number; sSum: number; up: number; down: number; na: number; scoreBase: number; emit: boolean; }
  const bases: Base[] = new Array(n);
  const baseScoreByTime = new Map<number, number>();
  let ci = -1, pi = -1, hi = -1; // merge-walk cursors
  let prevMacd: number | null = null;
  for (let i = 0; i < n; i++) {
    const t = candles[i].time;
    const ltp = closes[i];
    while (ci + 1 < cprPts.length && cprPts[ci + 1].time <= t) ci++;
    while (pi + 1 < cpocPts.length && cpocPts[pi + 1].time <= t) pi++;
    while (hi + 1 < hpocPts.length && hpocPts[hi + 1].time <= t) hi++;

    let wSum = 0, sSum = 0, up = 0, down = 0, na = 0;
    const add = (dir: TrendDir, w: number) => {
      if (dir === 'na') { na++; return; }
      if (!(w > 0)) return;
      wSum += w; sSum += w * (dir === 'up' ? 1 : dir === 'down' ? -1 : 0);
      if (dir === 'up') up++; else if (dir === 'down') down++;
    };
    const bandDir = (hiV: number, loV: number): TrendDir => (ltp > hiV ? 'up' : ltp < loV ? 'down' : 'neutral');

    const E3 = e3[i], E4 = e4[i];
    add((E3 != null && E4 != null) ? (ltp > E3 && ltp > E4 ? 'up' : ltp < E3 && ltp < E4 ? 'down' : 'neutral') : 'na', W.ema);
    const bnd = bandByTime.get(t);
    add(bnd ? bandDir(bnd.hi, bnd.lo) : 'na', W.avgCloud);
    const cp = ci >= 0 ? cprPts[ci] : null;
    add(cp ? bandDir(cp.tc, cp.bc) : 'na', W.cpr);
    const pp = pi >= 0 ? cpocPts[pi] : null;
    add(pp ? bandDir(pp.top, pp.bottom) : 'na', W.cpoc);
    const hp = hi >= 0 ? hpocPts[hi] : null;
    add(hp ? bandDir(hp.top, hp.bottom) : 'na', W.hpoc);
    const rv = rsArr[i], re = rsEma[i];
    add((rv != null && re != null && Number.isFinite(rv) && Number.isFinite(re)) ? (rv > re ? 'up' : rv < re ? 'down' : 'neutral') : 'na', W.relStrength);
    const pv = pctArr[i], pe = pctEma[i];
    add((pv != null && pe != null && Number.isFinite(pv) && Number.isFinite(pe)) ? (pv > pe ? 'up' : pv < pe ? 'down' : 'neutral') : 'na', W.chgN);
    const mv = macdArr[i];
    let macdDir: TrendDir = 'na';
    if (mv != null && Number.isFinite(mv)) { if (prevMacd != null) macdDir = mv > prevMacd ? 'up' : mv < prevMacd ? 'down' : 'neutral'; prevMacd = mv; }
    add(macdDir, W.macd);
    const je = jEmaArr[i], jv = jVwmaArr[i];
    add((Number.isFinite(je) && Number.isFinite(jv)) ? (je > jv ? 'up' : je < jv ? 'down' : 'neutral') : 'na', W.jhm);
    const wrZone = wrPctZoneAt(wrFast, wrSlow, i);
    add(wrZone === 'BUY' ? 'up' : wrZone === 'SHORT' ? 'down' : 'na', W.wrPct);
    // R120 score-independent latched voters.
    add(bolevDArr[i], (W.bolevD ?? 0));
    add(bolevWArr[i], (W.bolevW ?? 0));
    add(volArr[i], (W.vol ?? 0));

    const scoreBase = wSum > 0 ? Math.round((sSum / wSum) * 100) : 0;
    if (wSum > 0) baseScoreByTime.set(t, scoreBase);
    bases[i] = { time: t, wSum, sSum, up, down, na, scoreBase, emit: wSum > 0 };
  }

  // ── Pass 2: fold the avBand voter in (Effective Line from baseScore) ────────
  const avArr = avBandDirSeries(candles, baseScoreByTime, settings);
  const avW = Math.max(0, Number(W.avBand) || 0);
  const out: TrendSeriesPoint[] = [];
  for (let i = 0; i < n; i++) {
    const b = bases[i];
    if (!b) continue;
    let { wSum, sSum, up, down, na } = b;
    const av = avArr[i];
    if (av === 'na') { na++; }
    else if (avW > 0) { wSum += avW; sSum += avW * (av === 'up' ? 1 : av === 'down' ? -1 : 0); if (av === 'up') up++; else if (av === 'down') down++; }
    // Emit on the FINAL weight (base + avBand) — NOT just the base. Otherwise,
    // when avBand is the only ON constituent the base wSum is 0 and the bar would
    // be dropped, so avBand alone could never colour candles (the series would be
    // empty and bars fell back to raw price colour).
    if (wSum > 0) {
      const score = Math.round((sSum / wSum) * 100);
      out.push({ time: b.time, score, scoreBase: b.scoreBase, zone: zoneForScore(score, thresholds.strong, thresholds.probable), up, down, na });
    }
  }
  return out;
}
